import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import BulletButton from "./BulletButton";
import { PanelHeader } from "./Panel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBackwardStep,
  faForwardStep,
  faPause,
  faPlay,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { SPOTIFY_AUTH_CHANGED_EVENT } from "../services/spotifyAuth";
import {
  SpotifyService,
  type SpotifyTokenRow,
} from "../services/spotifyService";

const SDK_SCRIPT_ID = "spotify-web-playback-sdk";

type SpotifyPlaybackResponse = {
  is_playing?: boolean;
  device?: {
    id?: string;
    name?: string;
  };
  item?: {
    name?: string;
    artists?: Array<{ name: string }>;
    album?: {
      images?: Array<{ url: string }>;
    };
  };
};

type SpotifyDevicesResponse = {
  devices?: Array<{
    id?: string;
    name?: string;
    is_active?: boolean;
  }>;
};

function SpotifyPlayer() {
  const clientId = process.env.REACT_APP_SPOTIFY_CLIENT_ID;

  const spotifyService = useMemo(() => new SpotifyService(), []);
  const playerRef = useRef<Spotify.Player | null>(null);

  const [tokenData, setTokenData] = useState<SpotifyTokenRow | null>(null);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(true);
  const [isPremium, setIsPremium] = useState<boolean | null>(null);
  const [nowPlaying, setNowPlaying] = useState<string>("Nothing is playing");
  const [artistLine, setArtistLine] = useState<string | null>(null);
  const [activeDeviceName, setActiveDeviceName] = useState<string | null>(null);
  const [albumArtUrl, setAlbumArtUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [playPausing, setPlayPausing] = useState<boolean>(false);

  const loadTokenState = useCallback(async () => {
    try {
      const next = await spotifyService.getSpotifyToken();
      setTokenData(next);
    } catch {
      setTokenData(null);
    }
  }, [spotifyService]);

  useEffect(() => {
    loadTokenState();

    const onAuthChanged = () => {
      loadTokenState();
    };

    window.addEventListener(SPOTIFY_AUTH_CHANGED_EVENT, onAuthChanged);

    return () => {
      window.removeEventListener(SPOTIFY_AUTH_CHANGED_EVENT, onAuthChanged);
    };
  }, [loadTokenState]);

  const getValidAccessToken = useCallback(async (): Promise<string | null> => {
    return spotifyService.getValidAccessToken(clientId);
  }, [clientId, spotifyService]);

  const callSpotifyApi = useCallback(
    async (
      path: string,
      method = "GET",
      body?: Record<string, unknown>,
      options?: { allow404?: boolean },
    ) => {
      const accessToken = await getValidAccessToken();
      if (!accessToken) {
        throw new Error("Connect Spotify in Admin to use direct controls.");
      }

      const response = await fetch(`https://api.spotify.com/v1${path}`, {
        method,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      if (response.status === 204) {
        return null;
      }

      if (response.status === 404 && options?.allow404) {
        return null;
      }

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Spotify API failed (${response.status}).`);
      }

      return response.json();
    },
    [getValidAccessToken],
  );

  const applyPlaybackState = useCallback(
    (state: SpotifyPlaybackResponse | null) => {
      if (!state?.item) {
        setNowPlaying("Nothing is playing");
        setArtistLine(null);
        setIsPaused(true);
        setAlbumArtUrl(null);
        setActiveDeviceName(state?.device?.name || null);
        return;
      }

      const track = state.item;
      const artists = (track.artists || [])
        .map((artist) => artist.name)
        .join(", ");
      setNowPlaying(track.name || "Unknown");
      setArtistLine(artists || null);
      setIsPaused(!(state.is_playing ?? false));
      setActiveDeviceName(state.device?.name || null);
      setAlbumArtUrl(track.album?.images?.[0]?.url || null);
    },
    [],
  );

  const fetchPlaybackState = useCallback(async () => {
    try {
      const state = (await callSpotifyApi("/me/player", "GET", undefined, {
        allow404: true,
      })) as SpotifyPlaybackResponse | null;
      applyPlaybackState(state);
      setError(null);
      setPlayPausing(false);
    } catch (e) {
      const message =
        e instanceof Error
          ? e.message
          : "Failed to read Spotify playback state.";
      setError(message);
      setPlayPausing(false);
    }
  }, [applyPlaybackState, callSpotifyApi]);

  const handleTogglePlay = useCallback(async () => {
    setError(null);
    setPlayPausing(true);
    try {
      if (isPaused) {
        await callSpotifyApi("/me/player/play", "PUT");
      } else {
        await callSpotifyApi("/me/player/pause", "PUT");
      }
    } catch (e) {
      // Some Spotify player states can return a transient API error even when
      // playback changes; force a state refresh before surfacing an error.
      console.warn("Spotify toggle failed, refreshing state:", e);
    } finally {
      await fetchPlaybackState();
    }
  }, [callSpotifyApi, fetchPlaybackState, isPaused]);

  const handleNext = useCallback(async () => {
    setError(null);
    try {
      await callSpotifyApi("/me/player/next", "POST");
    } catch (e) {
      console.warn("Spotify next failed, refreshing state:", e);
    } finally {
      await fetchPlaybackState();
    }
  }, [callSpotifyApi, fetchPlaybackState]);

  const handlePrevious = useCallback(async () => {
    setError(null);
    try {
      await callSpotifyApi("/me/player/previous", "POST");
    } catch (e) {
      console.warn("Spotify previous failed, refreshing state:", e);
    } finally {
      await fetchPlaybackState();
    }
  }, [callSpotifyApi, fetchPlaybackState]);

  const handlePlayHere = useCallback(async () => {
    if (!deviceId) {
      setError("This web player is not ready yet.");
      return;
    }

    setError(null);

    const wait = (ms: number) =>
      new Promise((resolve) => window.setTimeout(resolve, ms));

    try {
      // Required by browsers before autoplay can start on this web player.
      if (playerRef.current?.activateElement) {
        await playerRef.current.activateElement();
      }
      if (playerRef.current?.connect) {
        await playerRef.current.connect();
      }

      // Resolve a currently-available device id for this web player.
      const resolveWebDeviceId = async (): Promise<string | null> => {
        const playerName = "Home Hub Web Player";

        for (let i = 0; i < 8; i += 1) {
          const devices = (await callSpotifyApi(
            "/me/player/devices",
          )) as SpotifyDevicesResponse | null;
          const list = devices?.devices || [];

          const exact = list.find((d) => d.id === deviceId)?.id;
          if (exact) return exact;

          const byName = list.find((d) => d.name === playerName)?.id;
          if (byName) return byName;

          await wait(300);
        }

        return null;
      };

      const targetDeviceId = await resolveWebDeviceId();
      if (!targetDeviceId) {
        throw new Error("Spotify web player device is not available yet.");
      }

      try {
        await callSpotifyApi("/me/player", "PUT", {
          device_ids: [targetDeviceId],
          play: true,
        });
      } catch {
        // Fallback path for accounts/devices that reject immediate play=true transfer.
        await callSpotifyApi("/me/player", "PUT", {
          device_ids: [targetDeviceId],
          play: false,
        });
        await wait(250);
        await callSpotifyApi(
          `/me/player/play?device_id=${targetDeviceId}`,
          "PUT",
        );
      }

      await fetchPlaybackState();
    } catch (e) {
      const message =
        e instanceof Error
          ? e.message
          : "Unable to transfer playback to this device.";
      setError(message);
    }
  }, [callSpotifyApi, deviceId, fetchPlaybackState]);

  useEffect(() => {
    if (!tokenData?.accessToken) {
      setIsPremium(null);
      setNowPlaying("Nothing is playing");
      setArtistLine(null);
      setAlbumArtUrl(null);
      setActiveDeviceName(null);
      return;
    }

    callSpotifyApi("/me")
      .then((profile) => {
        const product = (profile as { product?: string }).product;
        setIsPremium(product === "premium");
      })
      .catch(() => {
        setIsPremium(null);
      });
  }, [callSpotifyApi, tokenData?.accessToken]);

  useEffect(() => {
    if (!tokenData?.accessToken) {
      return;
    }

    void fetchPlaybackState();
    const intervalId = window.setInterval(() => {
      void fetchPlaybackState();
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [fetchPlaybackState, tokenData?.accessToken]);

  useEffect(() => {
    if (!tokenData?.accessToken || isPremium === false) {
      return;
    }

    const disconnectPlayer = () => {
      if (playerRef.current) {
        playerRef.current.disconnect();
        playerRef.current = null;
      }
    };

    const setupPlayer = () => {
      if (!window.Spotify || playerRef.current) {
        return;
      }

      const player = new window.Spotify.Player({
        name: "Home Hub Web Player",
        getOAuthToken: async (cb) => {
          const accessToken = await getValidAccessToken();
          if (accessToken) {
            cb(accessToken);
          }
        },
        volume: 0.5,
      });

      player.addListener("ready", ({ device_id }: { device_id: string }) => {
        setDeviceId(device_id);
        setError(null);
      });

      player.addListener("not_ready", () => {
        setDeviceId(null);
      });

      player.addListener("authentication_error", ({ message }) => {
        setError(message || "Spotify authentication error.");
      });

      player.addListener("account_error", ({ message }) => {
        setError(
          message ||
            "Spotify account error. Premium is required for direct controls.",
        );
      });

      player.addListener("player_state_changed", () => {
        void fetchPlaybackState();
      });

      player.connect();
      playerRef.current = player;
    };

    const existingScript = document.getElementById(SDK_SCRIPT_ID);
    if (existingScript) {
      if (window.Spotify) {
        setupPlayer();
      } else {
        window.onSpotifyWebPlaybackSDKReady = setupPlayer;
      }
      return disconnectPlayer;
    }

    const script = document.createElement("script");
    script.id = SDK_SCRIPT_ID;
    script.src = "https://sdk.scdn.co/spotify-player.js";
    script.async = true;
    document.body.appendChild(script);

    window.onSpotifyWebPlaybackSDKReady = setupPlayer;

    return disconnectPlayer;
  }, [
    fetchPlaybackState,
    getValidAccessToken,
    isPremium,
    tokenData?.accessToken,
  ]);

  const playPauseIcon = useMemo(() => {
    if (playPausing) {
      return faSpinner;
    }
    return isPaused ? faPlay : faPause;
  }, [isPaused, playPausing]);

  const statusText = useMemo(() => {
    if (!tokenData?.accessToken) {
      return "Not connected";
    }

    if (isPremium === false) {
      return "Premium required";
    }

    if (activeDeviceName) {
      return activeDeviceName;
    }

    return "Connected";
  }, [activeDeviceName, isPremium, tokenData?.accessToken]);

  const connected = Boolean(tokenData?.accessToken);
  const hasTrack = nowPlaying !== "Nothing is playing";
  const live = hasTrack && !isPaused;

  return (
    <div className={`audio ${live ? "is-live" : ""}`}>
      <PanelHeader
        label={
          <>
            Audio
            {live && (
              <span className="audio__eq" aria-label="Playing">
                <i />
                <i />
                <i />
                <i />
              </span>
            )}
          </>
        }
        meta={statusText}
        accent={live}
      />

      <div className="audio__track">
        {hasTrack && albumArtUrl ? (
          <img src={albumArtUrl} alt="" className="audio__cover" />
        ) : (
          <div className="audio__cover audio__cover--empty">
            {hasTrack ? "No art" : "—"}
          </div>
        )}
        <div className="audio__text">
          <div className="audio__title">
            {connected ? nowPlaying : "Connect Spotify in Admin"}
          </div>
          {artistLine && <div className="audio__artist">{artistLine}</div>}
        </div>
      </div>

      {error && <div className="audio__error">{error}</div>}

      <div className="audio__controls">
        <BulletButton
          onClick={handlePrevious}
          disabled={!connected}
          aria-label="Previous track"
        >
          <FontAwesomeIcon icon={faBackwardStep} />
        </BulletButton>
        <BulletButton
          className="audio__play"
          onClick={handleTogglePlay}
          disabled={!connected || playPausing}
          aria-label={isPaused ? "Play" : "Pause"}
        >
          <FontAwesomeIcon icon={playPauseIcon} spin={playPausing} />
        </BulletButton>
        <BulletButton
          onClick={handleNext}
          disabled={!connected}
          aria-label="Next track"
        >
          <FontAwesomeIcon icon={faForwardStep} />
        </BulletButton>
        <button
          type="button"
          className="audio__here"
          onClick={handlePlayHere}
          disabled={!connected}
        >
          Play here →
        </button>
      </div>
    </div>
  );
}

export default SpotifyPlayer;
